import { useEffect, useRef, useState, type JSX } from 'react';
import { SPECIES_META, type DuckColor, type DuckSpecies } from '@duck/contracts';

/**
 * The live mascot for the Final CTA — the REAL drawn duck (the same procedural {@link Critter} the office
 * renders), idling, so the page closes on the actual living mascot rather than a flat 🦆 emoji. It reuses
 * the exact, proven {@link GalleryCritter} lifecycle (lazy Pixi, IntersectionObserver gate, reduced-motion
 * safe, no-WebGL emoji fallback) — just larger and configurable, so it never spins idle GPU while the
 * visitor is up the page, and never crashes the page on teardown.
 *
 * Why a separate component (not GalleryCritter): the closing mascot wants its OWN size (`scale`) and a
 * generous reserved square that fills the headline's crown, independent of the tightly-tiled gallery wall.
 *
 * ACCESSIBILITY: purely decorative (the headline carries the meaning) → `aria-hidden`, never tabbable.
 * No layout shift: the parent reserves the square; the emoji fallback fills it from first paint.
 */
export function CtaCritter({
  species = 'duck',
  color = 'yellow',
  activity = 'idle',
  scale = 2.6,
}: {
  species?: DuckSpecies;
  color?: DuckColor;
  activity?: string;
  /**
   * Pixi scale CAP for the critter art (gallery uses 1.7). The mascot mounts with contain-fit, so the
   * actual scale is whatever fills the reserved square without cropping the beak/feet, up to this cap.
   */
  scale?: number;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // Only spin Pixi while the closer is on (or near) screen.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setVisible(e.isIntersecting);
      },
      { rootMargin: '160px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !visible) return;
    let cancelled = false;
    let handle: { destroy(): void } | null = null;

    void import('./gallery-critter-scene')
      .then(({ mountGalleryCritter }) => mountGalleryCritter(el, { species, color, activity, scale, fit: true }))
      .then((h) => {
        if (cancelled) {
          h?.destroy();
          return;
        }
        handle = h;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, [visible, species, color, activity, scale]);

  return (
    <div ref={ref} aria-hidden="true" className="relative h-full w-full">
      {/* Emoji fallback behind the canvas — the only thing visible if WebGL is unavailable. */}
      <span className="absolute inset-0 flex items-center justify-center text-6xl opacity-80">
        {SPECIES_META[species].emoji}
      </span>
    </div>
  );
}
